import { Grid } from '@mui/material';
import { WithStyles, useTheme, withStyles } from '@mui/styles';
import { SnackbarProvider } from '@tassat/text';
import { browserSessionStore } from '@tassat/utility';
import classnames from 'classnames';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import GlobalHeader from 'Containers/globalHeader';
import { FeaturesContext } from 'Lib/context/featuresContext';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getKillSwitchOn } from 'Selectors/killswitch';
import ExportHandler from 'SharedComponents/exportHandler/exportHandler';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import AppStateModals from 'SharedComponents/modals/AppStateModals';
import Sidebar from 'SharedComponents/sidebar';

import * as styles from './adminStyles.module.css';

interface IAdminPageProps extends WithStyles<typeof muiStyles>, RouteComponentProps {
    children?: React.ReactNode;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    shrink: {
        flexShrink: 1,
        flexBasis: 'auto'
    },
    wrapper: { height: '100%' },
    spacer: {
        minWidth: '1rem',
        backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter ||
            '#CFCFCF',
        boxShadow: 'inset -0.8rem 0px 0.8rem -1rem black'
    },
    sidebar: {
        flexBasis: '100px',
        minWidth: '100px',
        maxWidth: '100px'
    },
    mainContent: {
        height: '100%',
        flexGrow: 1,
        maxWidth: '100%',
        width: theme?.app?.clientTheme.layoutSettings.useGlobalHeader ? 'calc(100% - 106px)' : '100%'
    },
    scrollInnerContainer: {
        display: 'flex' as const,
        flexDirection: 'column' as const,
        flexGrow: 1,
        overflowY: 'auto' as const,
        height: '100%'
    },
    noScrollInnerContainer: {
        display: 'flex' as const,
        height: '100%'
    }
});

export function AdminPage(props: IAdminPageProps) {
    const {
        classes,
        children,
        location
    } = props;

    const killSwitch = useAppSelector(getKillSwitchOn);

    const muiTheme = useTheme<ApplicationMuiTheme>();
    const [features] = useFeatures();
    const path = React.useMemo(() => location.pathname, [location.pathname]);

    React.useEffect(() => {
        window.addEventListener('beforeunload', (event) => {
            browserSessionStore.setItem('browser_refresh', 'true');
        });
    }, []);

    const cssVariables = React.useMemo(() => {
        const variables = {};
        if (!muiTheme?.app) return;

        variables['--client-color-primary'] = muiTheme.app.clientTheme.colors.primaryColor;
        variables['--client-color-primary-light'] = muiTheme.app.clientTheme.colors.primaryColorLight;
        variables['--client-color-primary-dark'] = muiTheme.app.clientTheme.colors.primaryColorDark;
        variables['--client-color-highlight-1'] = muiTheme.app.clientTheme.colors.highlightColor1;
        variables['--client-color-highlight-2'] = muiTheme.app.clientTheme.colors.highlightColor2;
        variables['--client-color-highlight-3'] = muiTheme.app.clientTheme.colors.highlightColor3;
        variables['--application-color-background-primary'] = muiTheme.app.applicationColors.backgroundPrimary;
        variables['--application-color-background-tos'] = muiTheme.app.applicationColors.backgroundTOS;
        variables['--application-color-disabled-item'] = muiTheme.app.applicationColors.disabledItemColor;
        variables['--application-color-grey-dark'] = muiTheme.app.applicationColors.enumColors.greyDark;
        variables['--application-color-off-white'] = muiTheme.app.applicationColors.enumColors.offWhite;
        variables['--application-color-agGrid-lockedRow'] = muiTheme.app.applicationColors.agGridHover.lockedRow;
        variables['--application-color-agGrid-pendingRow'] = muiTheme.app.applicationColors.agGridHover.pendingRow;
        variables['--application-color-agGrid-nthChild'] = muiTheme.app.applicationColors.agGridHover.nthChild;
        variables['--application-color-agGrid-headerCell'] = muiTheme.app.applicationColors.agGridHover.headerCell;

        return variables;
    }, [muiTheme]);

    return (
        <ExportHandler>
            <FeaturesContext.Provider value={features}>
                <SnackbarProvider>
                    <Grid
                        className={classes.wrapper}
                        container
                        direction='row'
                        item
                        spacing={0}
                        style={cssVariables}
                        wrap='nowrap'
                    >
                        <Grid
                            className={classes.sidebar}
                            item
                            md={1}
                        >
                            <Sidebar admin={true} />
                        </Grid>
                        {muiTheme?.app?.clientTheme.layoutSettings.useGlobalHeader &&
                            <div className={classes.spacer} />}
                        <Grid
                            className={classes.mainContent}
                            container
                            direction='column'
                            item
                            md={11}
                            spacing={0}
                            wrap='nowrap'
                        >
                            {muiTheme?.app?.clientTheme.layoutSettings.useGlobalHeader &&
                                <GlobalHeader />}
                            <div className={classes.scrollInnerContainer}>
                                {killSwitch &&
                                    <div>
                                        <div className={classnames(styles.errorMessage, styles.centeredContainer)}>
                                            <strong>Transaction features are temporarily unavailable. </strong>
                                        </div>
                                    </div>}
                                {children}
                            </div>
                        </Grid>
                        <AppStateModals />
                    </Grid>
                </SnackbarProvider>
            </FeaturesContext.Provider>
        </ExportHandler>
    );
}

export default withRouter(withStyles(muiStyles)(AdminPage));